// Friends + DMs hub. Mounted when the home button is active. Three
// sub-views via tabs:
//
//   - "All"       : every accepted friend, online + offline
//   - "Online"    : only friends whose status !== 'offline'
//   - "Pending"   : incoming + outgoing requests with accept /
//                   reject / cancel actions
//   - "Blocked"   : blocked accounts with profile and unblock actions
//   - "Add Friend": resolves an @username or Account ID and sends a request
//
// Selecting a DM swaps the right pane to its encrypted message view.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  Crown,
  ExternalLink,
  Inbox,
  Lock,
  MessageCircle,
  Phone,
  PhoneCall,
  PhoneMissed,
  Pin,
  Popcorn,
  Search,
  Send,
  ShieldOff,
  UserPlus,
  UserMinus,
  Users,
  Video,
  X,
} from "lucide-react";
import type { Participant } from "livekit-client";

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  GlassMaterial,
  Input,
  Label,
  ScrollArea,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@link/ui";

import type { E2eRecipient } from "@link/crypto";

import { ComposerActions } from "./messages/ComposerActions";
import {
  ChatFileDropOverlay,
  useChatFileDrop,
} from "./messages/ChatFileDropZone";
import { MessageItem } from "./messages/MessageItem";
import { groupsWithPrevious } from "../lib/message-presentation";
import { useComposerAutoGrow, useComposerDraft, useComposerInsert } from "../lib/composer";
import { beginUpdateActivity } from "../lib/update-activity";
import { BadgeRow } from "./BadgeRow";
import type { MemberProfile } from "./MemberProfilePopover";
import { StatusDot } from "./StatusDot";
import { UserActionsMenu } from "./UserActionsMenu";
import { UserProfileModal } from "./UserProfileModal";
import { AccountInbox } from "./AccountInbox";
import { api, ApiCallError, type CallHistoryEntry } from "../lib/api";
import { verifiedBadgeOnly } from "../lib/badges";
import { hasOpenLocalCall } from "../lib/call-lifecycle";
import {
  dmComposerPlaceholder,
  dmAutoScrollBehavior,
  type DmTimelinePosition,
} from "../lib/chat-interactions";
import { useMessages } from "../state/data";
import { useUploads } from "../state/Uploads";
import {
  participantVideoTrack,
  participantIsScreenSharing,
  useMediaSession,
  type WatchService,
} from "../state/MediaSession";
import { CallHeader, CallParticipantBubble, CallSurface } from "./media/CallSurface";
import { CallControls } from "./media/CallControls";
import { CallVideoTile } from "./media/CallVideoTile";
import { ComposerReplyPreview } from "./messages/ReplyPreview";
import { useSelf } from "../state/SelfSession";
import { CallError } from "./media/CallError";
import type {
  DmView,
  FriendStatus,
  FriendView,
  HomeTab,
  MessageView,
  PendingFriendView,
} from "../types/ui";

type FriendsTab = "online" | "all" | "pending" | "blocked" | "add";

const DM_WATCH_SERVICES: Array<{ id: WatchService; label: string }> = [
  { id: "youtube", label: "YouTube" },
  { id: "netflix", label: "Netflix" },
  { id: "disney", label: "Disney+" },
  { id: "prime", label: "Prime Video" },
  { id: "hulu", label: "Hulu" },
  { id: "other", label: "Other" },
];

interface Props {
  tab: HomeTab;
  onSelectTab: (tab: HomeTab) => void;
  self: { name: string; tag: string; accountXid: string };
  friends: FriendView[];
  pending: PendingFriendView[];
  blocked: FriendView[];
  dms: DmView[];
  activeDmXid: string | null;
  onSelectDm: (xid: string) => void;
  onStartDm: (accountXid: string) => void;
  initialDmDraft?: string;
  sendInitialDmDraft?: boolean;
  onConsumeDmDraft?: (dmXid: string) => void;
  /** Optional refresh hook — called after a friend mutation lands. */
  onFriendsChanged?: () => void;
  onDmsChanged?: () => void;
}

const STATUS_DOT: Record<FriendStatus, string> = {
  online: "bg-emerald-400",
  away: "bg-amber-400",
  busy: "bg-red-400",
  offline: "bg-cloud-300",
};

const STATUS_LABEL: Record<FriendStatus, string> = {
  online: "Online",
  away: "Away",
  busy: "Do not disturb",
  offline: "Offline",
};

export function FriendsView(props: Props) {
  if (props.tab === "inbox") return <AccountInbox />;
  // An active DM replaces the Friends list with its encrypted thread.
  if (props.tab === "dm" && props.activeDmXid !== null) {
    const dm = props.dms.find((d) => d.xid === props.activeDmXid);
    if (dm !== undefined) {
      return (
        <DmPane
          dm={dm}
          self={props.self}
          friends={props.friends}
          initialDraft={props.initialDmDraft}
          sendInitialDraft={props.sendInitialDmDraft}
          onDraftConsumed={props.onConsumeDmDraft}
          onDmsChanged={props.onDmsChanged}
        />
      );
    }
  }

  return (
    <FriendsHub
      friends={props.friends}
      pending={props.pending}
      blocked={props.blocked}
      onStartDm={props.onStartDm}
      onFriendsChanged={props.onFriendsChanged}
    />
  );
}

// ---------------------------------------------------------------------------
// Friends hub
// ---------------------------------------------------------------------------

interface FriendsHubProps {
  friends: FriendView[];
  pending: PendingFriendView[];
  blocked: FriendView[];
  onStartDm: (xid: string) => void;
  onFriendsChanged?: () => void;
}

function FriendsHub({ friends, pending, blocked, onStartDm, onFriendsChanged }: FriendsHubProps) {
  const [tab, setTab] = useState<FriendsTab>("online");
  useEffect(() => {
    const openAdd = (): void => setTab("add");
    window.addEventListener("link:open-add-friend", openAdd);
    return () => window.removeEventListener("link:open-add-friend", openAdd);
  }, []);
  const [filter, setFilter] = useState("");
  const [selectedFriend, setSelectedFriend] = useState<FriendView | null>(null);

  const onlineFriends = friends.filter((f) => f.status !== "offline");
  const visibleFriends =
    tab === "online" ? onlineFriends : tab === "all" ? friends : friends;
  const filtered = visibleFriends.filter((f) =>
    f.name.toLowerCase().includes(filter.toLowerCase()),
  );

  return (
    <main className="glass-content relative flex flex-1 flex-col">
      {/* Header */}
      <header className="glass-toolbar liquid-lens-host relative flex h-14 items-center gap-3 border-b border-strawberry-100 bg-white/60 px-4">
        <GlassMaterial />
        <Users className="h-5 w-5 text-cloud-500" />
        <span className="text-base font-semibold text-cloud-700">Friends</span>
        <div className="hidden h-6 w-px bg-strawberry-200 sm:block" />
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as FriendsTab)}
          className="hidden md:block"
        >
          <TabsList className="bg-transparent p-0 gap-1">
            <FriendsTabTrigger value="online" label="Online" count={onlineFriends.length} />
            <FriendsTabTrigger value="all" label="All" count={friends.length} />
            <FriendsTabTrigger
              value="pending"
              label="Pending"
              count={pending.length}
              highlight={pending.some((p) => p.direction === "incoming")}
            />
            <FriendsTabTrigger value="blocked" label="Blocked" count={blocked.length} />
            <TabsTrigger
              value="add"
              className="bg-emerald-100 text-emerald-700 data-[state=active]:bg-emerald-500 data-[state=active]:text-white"
            >
              Add Friend
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </header>

      {/* Body */}
      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as FriendsTab)}
        className="flex flex-1 flex-col"
      >
        {/* Mobile tab strip */}
        <TabsList className="mx-3 my-3 flex w-[calc(100%-1.5rem)] shrink-0 justify-start gap-1 overflow-x-auto md:hidden">
          <TabsTrigger value="online" className="shrink-0">Online</TabsTrigger>
          <TabsTrigger value="all" className="shrink-0">All</TabsTrigger>
          <TabsTrigger value="pending" className="shrink-0">Pending</TabsTrigger>
          <TabsTrigger value="blocked" className="shrink-0">Blocked</TabsTrigger>
          <TabsTrigger value="add" className="shrink-0">Add</TabsTrigger>
        </TabsList>

        <TabsContent value="online" className="mt-0 flex-1 px-3 pb-4 sm:px-6">
          <FriendsList
            heading={`Online — ${onlineFriends.length}`}
            filter={filter}
            onFilterChange={setFilter}
            friends={filtered}
            onMessage={onStartDm}
            onOpenProfile={setSelectedFriend}
          />
        </TabsContent>
        <TabsContent value="all" className="mt-0 flex-1 px-3 pb-4 sm:px-6">
          <FriendsList
            heading={`All friends — ${friends.length}`}
            filter={filter}
            onFilterChange={setFilter}
            friends={filtered}
            onMessage={onStartDm}
            onOpenProfile={setSelectedFriend}
          />
        </TabsContent>
        <TabsContent value="pending" className="mt-0 flex-1 px-3 pb-4 sm:px-6">
          <PendingList
            pending={pending}
            onChanged={onFriendsChanged}
            onOpenProfile={setSelectedFriend}
          />
        </TabsContent>
        <TabsContent value="blocked" className="mt-0 flex-1 px-3 pb-4 sm:px-6">
          <BlockedList
            blocked={blocked}
            onChanged={onFriendsChanged}
            onOpenProfile={setSelectedFriend}
          />
        </TabsContent>
        <TabsContent value="add" className="mt-0 flex-1 px-3 pb-4 sm:px-6">
          <AddFriendPanel onChanged={onFriendsChanged} />
        </TabsContent>
      </Tabs>
      {selectedFriend !== null ? (
        <UserProfileModal
          open
          member={friendToMemberProfile([...friends, ...pending, ...blocked].find((friend) => friend.xid === selectedFriend.xid) ?? selectedFriend)}
          onOpenChange={(open) => {
            if (!open) setSelectedFriend(null);
          }}
        />
      ) : null}
    </main>
  );
}

function FriendsTabTrigger({
  value,
  label,
  count,
  highlight,
}: {
  value: string;
  label: string;
  count: number;
  highlight?: boolean;
}) {
  return (
    <TabsTrigger
      value={value}
      className="group relative border border-transparent bg-transparent px-3 text-cloud-500 shadow-none ring-0 hover:bg-cloud-100 hover:text-cloud-800 data-[state=active]:border-cloud-400 data-[state=active]:!bg-cloud-200/70 data-[state=active]:!text-cloud-900 data-[state=active]:!shadow-none data-[state=active]:ring-1 data-[state=active]:ring-cloud-300"
    >
      <span>{label}</span>
      {count > 0 && (
        <span className="ml-1.5 rounded-full bg-strawberry-100 px-1.5 py-0 text-[10px] font-semibold text-strawberry-700 group-data-[state=active]:bg-cloud-300 group-data-[state=active]:text-cloud-900">
          {count}
        </span>
      )}
      {highlight && (
        <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
      )}
    </TabsTrigger>
  );
}

interface FriendsListProps {
  heading: string;
  filter: string;
  onFilterChange: (v: string) => void;
  friends: FriendView[];
  onMessage: (xid: string) => void;
  onOpenProfile: (friend: FriendView) => void;
}

function FriendsList({
  heading,
  filter,
  onFilterChange,
  friends,
  onMessage,
  onOpenProfile,
}: FriendsListProps) {
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 pb-3 pt-3">
        <div className="ui-glass-input flex flex-1 items-center gap-2 rounded-lg border border-strawberry-100 bg-white/80 px-3 py-2">
          <Search className="h-4 w-4 text-cloud-400" />
          <input
            type="text"
            value={filter}
            onChange={(e) => onFilterChange(e.target.value)}
            placeholder="Search friends"
            className="flex-1 bg-transparent text-sm text-cloud-700 outline-none placeholder:text-cloud-400"
          />
        </div>
      </div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-cloud-500">
        {heading}
      </p>
      <ScrollArea className="-mx-2 flex-1 px-2">
        {friends.length === 0 ? (
          <EmptyState
            icon={<Users className="h-8 w-8 text-cloud-400" />}
            title="No friends here yet"
            body="Add friends from the Add Friend tab to start a conversation."
          />
        ) : (
          <ul className="space-y-1">
            {friends.map((f) => (
              <FriendRow
                key={f.xid}
                friend={f}
                onMessage={onMessage}
                onOpenProfile={onOpenProfile}
              />
            ))}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
}

function FriendRow({
  friend,
  onMessage,
  onOpenProfile,
}: {
  friend: FriendView;
  onMessage: (xid: string) => void;
  onOpenProfile: (friend: FriendView) => void;
}) {
  return (
    <li className="group flex items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-cloud-200/70">
      <button
        type="button"
        onClick={() => onOpenProfile(friend)}
        className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloud-400"
        aria-label={`Open ${friend.name}'s profile`}
      >
        <div className="relative">
          <Avatar className="h-9 w-9">
            {friend.avatarUrl != null ? <AvatarImage src={friend.avatarUrl} alt="" /> : null}
            <AvatarFallback className="text-[11px]">
              {friend.name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span
            className={cn(
              "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white",
              STATUS_DOT[friend.status],
            )}
          />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-cloud-700">
            <span className="inline-flex items-center gap-1">
              {friend.name}
              <BadgeRow badges={verifiedBadgeOnly(friend.badges ?? [])} />
            </span>
          </p>
          <p className="truncate text-[11px] text-cloud-500">
            {friend.note ?? STATUS_LABEL[friend.status]}
          </p>
        </div>
      </button>
      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onMessage(friend.xid)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-cloud-500 ring-1 ring-strawberry-100 hover:text-strawberry-600"
            >
              <MessageCircle className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Message</TooltipContent>
        </Tooltip>
        <UserActionsMenu
          accountXid={friend.xid}
          displayName={friend.name}
          buttonClassName="h-8 w-8 bg-white text-cloud-500 ring-strawberry-100 hover:text-strawberry-600"
        />
      </div>
    </li>
  );
}

function PendingList({
  pending,
  onChanged,
  onOpenProfile,
}: {
  pending: PendingFriendView[];
  onChanged?: () => void;
  onOpenProfile: (friend: FriendView) => void;
}) {
  if (pending.length === 0) {
    return (
      <EmptyState
        icon={<Inbox className="h-8 w-8 text-cloud-400" />}
        title="No pending requests"
        body="Incoming and outgoing friend requests will show up here."
      />
    );
  }
  return (
    <div className="flex h-full flex-col pt-3">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-cloud-500">
        Pending — {pending.length}
      </p>
      <ScrollArea className="-mx-2 flex-1 px-2">
        <ul className="space-y-1">
          {pending.map((p) => (
            <li
              key={p.xid}
              className="flex items-center gap-2 rounded-xl px-2 py-1.5 transition-colors hover:bg-cloud-200/70"
            >
              <button
                type="button"
                onClick={() => onOpenProfile(p)}
                className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloud-400"
                aria-label={`Open ${p.name}'s profile`}
              >
                <Avatar className="h-9 w-9">
                  {p.avatarUrl != null ? <AvatarImage src={p.avatarUrl} alt="" /> : null}
                  <AvatarFallback className="text-[11px]">
                    {p.name.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-cloud-700">
                    <span className="inline-flex items-center gap-1">
                      {p.name}
                      <BadgeRow badges={verifiedBadgeOnly(p.badges ?? [])} />
                    </span>
                  </p>
                  <p className="text-[11px] text-cloud-500">
                    {p.direction === "incoming"
                      ? "Incoming friend request"
                      : "Outgoing friend request"}
                  </p>
                </div>
              </button>
              <div className="flex items-center gap-1">
                {p.direction === "incoming" ? (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => {
                            void api.friends
                              .accept(p.xid)
                              .then(() => onChanged?.())
                              .catch(() => undefined);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-emerald-600 ring-1 ring-emerald-200 hover:bg-emerald-50"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Accept</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={() => {
                            void api.friends
                              .reject(p.xid)
                              .then(() => onChanged?.())
                              .catch(() => undefined);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-red-500 ring-1 ring-red-200 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Reject</TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => {
                          // Outgoing requests are cancelled with
                          // the same reject endpoint.
                          void api.friends
                            .reject(p.xid)
                            .then(() => onChanged?.())
                            .catch(() => undefined);
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-red-500 ring-1 ring-red-200 hover:bg-red-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Cancel</TooltipContent>
                  </Tooltip>
                )}
              </div>
            </li>
          ))}
        </ul>
      </ScrollArea>
    </div>
  );
}

function BlockedList({
  blocked,
  onChanged,
  onOpenProfile,
}: {
  blocked: FriendView[];
  onChanged?: () => void;
  onOpenProfile: (friend: FriendView) => void;
}) {
  const [busyXid, setBusyXid] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (blocked.length === 0) {
    return (
      <EmptyState
        icon={<ShieldOff className="h-8 w-8 text-cloud-400" />}
        title="No blocked accounts"
        body="Accounts you block from a profile or direct message will appear here."
      />
    );
  }

  return (
    <div className="flex h-full flex-col pt-3">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-cloud-500">
        Blocked — {blocked.length}
      </p>
      {error !== null ? <p className="mb-2 text-xs text-red-600">{error}</p> : null}
      <ScrollArea className="-mx-2 flex-1 px-2">
        <ul className="space-y-1">
          {blocked.map((account) => (
            <li key={account.xid} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-cloud-200/70">
              <button
                type="button"
                onClick={() => onOpenProfile(account)}
                className="flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cloud-400"
              >
                <Avatar className="h-9 w-9">
                  {account.avatarUrl != null ? <AvatarImage src={account.avatarUrl} alt="" /> : null}
                  <AvatarFallback className="text-[11px]">{account.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-cloud-700">
                    <span className="inline-flex items-center gap-1">
                      {account.name}
                      <BadgeRow badges={verifiedBadgeOnly(account.badges ?? [])} />
                    </span>
                  </p>
                  <p className="truncate text-[11px] text-cloud-500">@{account.username ?? account.xid}</p>
                </div>
              </button>
              <Button
                variant="outline"
                size="sm"
                disabled={busyXid === account.xid}
                onClick={() => {
                  setBusyXid(account.xid);
                  setError(null);
                  void api.friends
                    .unblock(account.xid)
                    .then(() => onChanged?.())
                    .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Couldn't unblock that account."))
                    .finally(() => setBusyXid(null));
                }}
              >
                {busyXid === account.xid ? "Unblocking…" : "Unblock"}
              </Button>
            </li>
          ))}
        </ul>
      </ScrollArea>
    </div>
  );
}

function friendToMemberProfile(friend: FriendView): MemberProfile {
  const memberSince = friend.memberSince === undefined
    ? undefined
    : new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(friend.memberSince));

  return {
    name: friend.name,
    tag: friend.xid,
    ...(friend.username !== undefined ? { username: friend.username } : {}),
    ...(friend.avatarUrl !== undefined ? { avatarUrl: friend.avatarUrl } : {}),
    ...(friend.bannerColor !== undefined ? { bannerColor: friend.bannerColor } : {}),
    ...(friend.bannerMode !== undefined ? { bannerMode: friend.bannerMode } : {}),
    ...(friend.bannerSecondaryColor !== undefined
      ? { bannerSecondaryColor: friend.bannerSecondaryColor }
      : {}),
    ...(friend.pronouns !== undefined ? { pronouns: friend.pronouns } : {}),
    status: friend.status,
    bio: null,
    roles: [],
    mutualSpaces: [],
    badges: friend.badges,
    communityTag: friend.communityTag,
    ...(memberSince !== undefined ? { memberSince } : {}),
  };
}

function AddFriendPanel({ onChanged }: { onChanged?: () => void }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Accept either a bare Account ID or an `@username`.
  const submit = (): void => {
    setError(null);
    setSuccess(null);
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      setError("Enter an @username or Account ID");
      return;
    }
    const accountXid = trimmed.replace(/^@/, "");
    setSubmitting(true);
    api.friends
      .request({ account_xid: accountXid })
      .then(() => {
        setSuccess(`Friend request sent to @${accountXid}`);
        setValue("");
        onChanged?.();
      })
      .catch((err: unknown) => {
        if (err instanceof ApiCallError) {
          setError(err.envelope?.message ?? `Request failed (HTTP ${err.status})`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Friend request failed.");
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="mx-auto flex h-full max-w-2xl flex-col pt-6">
      <div className="rounded-2xl border border-strawberry-100 bg-white/80 p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-strawberry-gradient text-white shadow-sm shadow-strawberry-200/40">
            <UserPlus className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-cloud-700">
              Add Friend
            </h2>
            <p className="mt-1 text-sm text-cloud-500">
              Add a friend with their unique <code className="rounded bg-strawberry-50 px-1 py-0.5 text-strawberry-700">@username</code>{" "}
              or Account ID. Usernames can only be changed once every 7 days.
            </p>

            <form
              className="mt-4 flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <div className="flex-1">
                <Label htmlFor="add-friend-id" className="sr-only">
                  Username or Account ID
                </Label>
                <Input
                  id="add-friend-id"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="@name"
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                size="lg"
                className="h-11 px-5"
                disabled={value.trim().length === 0 || submitting}
              >
                <Send className="h-4 w-4" />
                {submitting ? "Sending…" : "Send Request"}
              </Button>
            </form>

            {error !== null && (
              <p className="mt-3 text-xs font-medium text-red-600">
                {error}
              </p>
            )}
            {success !== null && (
              <p className="mt-3 text-xs font-medium text-emerald-700">
                {success}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <InfoTile
          title="One unique username."
          body="Every @username is globally unique and protected by a seven-day change cooldown."
        />
        <InfoTile
          title="Block anytime."
          body="If someone is being weird, block them from their profile."
        />
        <InfoTile
          title="No public directory."
          body="Discoverability is opt-in. You're only found by exact @name or Account ID."
        />
      </div>
    </div>
  );
}

function InfoTile({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-strawberry-100 bg-white/70 p-4">
      <p className="text-sm font-semibold text-cloud-700">{title}</p>
      <p className="mt-1 text-xs text-cloud-500">{body}</p>
    </div>
  );
}

function EmptyState({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-8 py-12 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-strawberry-50">
        {icon}
      </div>
      <p className="text-base font-semibold text-cloud-700">{title}</p>
      <p className="max-w-md text-sm text-cloud-500">{body}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DM message pane
// ---------------------------------------------------------------------------

function DmPane({
  dm,
  self,
  friends,
  initialDraft,
  sendInitialDraft = false,
  onDraftConsumed,
  onDmsChanged,
}: {
  dm: DmView;
  self: { name: string; tag: string; accountXid: string };
  friends: FriendView[];
  initialDraft?: string;
  sendInitialDraft?: boolean;
  onDraftConsumed?: (dmXid: string) => void;
  onDmsChanged?: () => void;
}) {
  const selfSession = useSelf();
  const [quickEdit, setQuickEdit] = useState<MessageView | null>(null);
  const [replyTo, setReplyTo] = useState<MessageView | null>(null);
  const [pinnedOpen, setPinnedOpen] = useState(false);
  const [pinnedXids, setPinnedXids] = useState<string[]>([]);
  const [composerFocused, setComposerFocused] = useState(false);
  const [fullProfileOpen, setFullProfileOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [callHistory, setCallHistory] = useState<CallHistoryEntry[]>([]);
  const [minimizedCallXid, setMinimizedCallXid] = useState<string | null>(null);
  useEffect(() => {
    const returnToCall = (event: Event) => {
      const detail = (event as CustomEvent<{ dmXid?: string; focusCall?: boolean }>).detail;
      if (detail?.dmXid === dm.xid && detail.focusCall) setMinimizedCallXid(null);
    };
    window.addEventListener("link:open-dm-xid", returnToCall);
    return () => window.removeEventListener("link:open-dm-xid", returnToCall);
  }, [dm.xid]);
  const returnToCallButton = useRef<HTMLButtonElement>(null);
  const consumedDraft = useRef<string | null>(null);
  const timelineEnd = useRef<HTMLDivElement | null>(null);
  const previousTimelinePosition = useRef<DmTimelinePosition | null>(null);
  const draftStorageKey = `link:composer-draft:${self.accountXid}:dm:${dm.xid}`;
  const [draft, setDraft] = useComposerDraft(draftStorageKey, quickEdit !== null);
  const composerInput = useRef<HTMLTextAreaElement>(null);
  useComposerAutoGrow(composerInput, draft);
  const insertIntoDraft = useComposerInsert(composerInput, draft, setDraft);

  useEffect(() => {
    setQuickEdit(null);
    setReplyTo(null);
  }, [draftStorageKey]);

  const primaryParticipant = dm.participants[0];
  const primaryFriend =
    primaryParticipant === undefined
      ? undefined
      : friends.find((friend) => friend.xid === primaryParticipant.xid);
  const profileMember: MemberProfile | null = primaryFriend !== undefined
    ? friendToMemberProfile(primaryFriend)
    : primaryParticipant === undefined
      ? null
      : friendToMemberProfile(primaryParticipant);
  const selfFriend: FriendView = {
    xid: selfSession.identity.accountXid,
    name: selfSession.identity.name,
    username: selfSession.identity.username,
    avatarUrl: selfSession.identity.avatarUrl,
    bannerColor: selfSession.identity.bannerColor,
    bannerMode: selfSession.identity.bannerMode,
    bannerSecondaryColor: selfSession.identity.bannerSecondaryColor,
    pronouns: selfSession.identity.pronouns,
    tag: selfSession.identity.accountXid.slice(-4),
    status: selfSession.status === "invisible" ? "offline" : selfSession.status,
    note: selfSession.identity.customStatus,
    memberSince: selfSession.identity.memberSince,
    badges: selfSession.identity.badges,
  };

  // Convert the DM's recipients (base64 from the API) into the
  // shape `useMessages` expects. When `dm.recipients` is undefined
  // A missing recipient set keeps the hook idle instead of attempting an
  // incomplete encrypted send.
  const recipients = useMemo(() => {
    if (dm.recipients === undefined) return [] as E2eRecipient[];
    return dm.recipients.map((r) => ({
      accountIdHash: b64ToBytes(r.accountIdHashB64),
      identityPublicKey: b64ToBytes(r.publicKeyB64),
    }));
  }, [dm.recipients]);

  const live = useMessages(
    dm.xid,
    [],
    "e2e-multi",
    recipients,
    self.accountXid,
    self.name,
  );
  const messages = live.messages;
  const uploads = useUploads();
  const media = useMediaSession();
  const dmCall = [media.activeCall, media.outgoingCall, media.incomingCall]
    .find((call) => call?.dm_xid === dm.xid) ?? null;
  const callExpanded = dmCall !== null && minimizedCallXid !== dmCall.call_xid;
  const thisDmConnected = media.target?.context === "dm" && media.target.xid === dm.xid && media.connected;
  const anotherCallIsOpen = hasOpenLocalCall([
    media.activeCall,
    media.outgoingCall,
    media.incomingCall,
  ]);
  const callStateKey = [
    media.activeCall?.call_xid,
    media.activeCall?.status,
    media.outgoingCall?.call_xid,
    media.outgoingCall?.status,
    media.incomingCall?.call_xid,
    media.incomingCall?.status,
    media.callNotice?.id,
    ...media.missedCalls.map((call) => `${call.call_xid}:${call.status}`),
  ].join("|");

  useEffect(() => {
    let cancelled = false;
    void api.calls
      .history(dm.xid)
      .then(({ calls }) => {
        if (!cancelled) setCallHistory(calls);
      })
      .catch(() => {
        if (!cancelled) setCallHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [callStateKey, dm.xid]);

  const timeline = useMemo(() => {
    const messageItems = messages.map((message, index) => ({
      kind: "message" as const,
      key: `message:${message.xid}`,
      at: message.createdAt === undefined
        ? Number.MAX_SAFE_INTEGER - messages.length + index
        : Date.parse(message.createdAt),
      message,
    }));
    const callItems = callHistory
      .filter((call) => call.ended_at !== null)
      .map((call) => ({
        kind: "call" as const,
        key: `call:${call.call_xid}`,
        at: Date.parse(call.started_at),
        call,
      }));
    return [...messageItems, ...callItems].sort((left, right) => left.at - right.at);
  }, [callHistory, messages]);
  const latestTimelineKey = timeline.at(-1)?.key ?? null;

  useEffect(() => {
    const current = { dmXid: dm.xid, latestItemKey: latestTimelineKey };
    const behavior = dmAutoScrollBehavior(previousTimelinePosition.current, current);
    previousTimelinePosition.current = current;
    if (behavior === null) return;

    const frame = window.requestAnimationFrame(() => {
      timelineEnd.current?.scrollIntoView({ behavior, block: "end" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [dm.xid, latestTimelineKey]);

  const queueFiles = (files: File[]): void => {
    if (live.status !== "ready" || recipients.length === 0) {
      setUploadError("This conversation is still being secured. Try again in a moment.");
      return;
    }
    setUploadError(null);
    const finishUpload = beginUpdateActivity();
    void (async () => {
      try {
        for (const file of files) {
          const result = await uploads.start({
            file,
            channelXid: dm.xid,
            e2eRecipients: recipients,
          });
          await live.sendFile({
            attachmentXid: result.attachmentXid,
            filename: result.filename,
            mime: result.mime,
            byteSize: result.byteSize,
          });
        }
      } catch (cause) {
        setUploadError(cause instanceof Error ? cause.message : "Upload failed.");
      } finally { finishUpload(); }
    })();
  };
  const fileDrop = useChatFileDrop({
    onFiles: queueFiles,
    disabled: live.status !== "ready" || recipients.length === 0,
  });

  useEffect(() => {
    if (initialDraft === undefined) return;
    const key = `${dm.xid}:${initialDraft}`;
    if (consumedDraft.current === key) return;
    if (sendInitialDraft && live.status !== "ready") return;
    consumedDraft.current = key;
    if (sendInitialDraft) {
      void live.send(initialDraft);
      setDraft("");
    } else {
      setDraft(initialDraft);
    }
    onDraftConsumed?.(dm.xid);
  }, [dm.xid, initialDraft, live, onDraftConsumed, sendInitialDraft]);

  // E2E privacy posture: DMs always render in their own surface
  // and never leak a body to anything else (channel-list previews
  // live at the sidebar level and route through `renderForSurface`
  // already).
  const directPresence = !dm.isGroup ? dm.participants[0]?.status : undefined;
  const loadPins = (): void => { setPinnedOpen((current) => !current); if (!pinnedOpen) void api.dms.pins(dm.xid).then((result) => setPinnedXids(result.message_xids)).catch(() => setPinnedXids([])); };

  return (
    <main {...fileDrop.handlers} className="glass-content relative flex min-h-0 min-w-0 flex-1 flex-col bg-white">
      <ChatFileDropOverlay
        active={fileDrop.active && !callExpanded}
        disabled={live.status !== "ready"}
        destination={dm.isGroup ? dm.name : `your chat with ${dm.name}`}
      />
      {dmCall !== null ? (
        <DmCallStage key={dmCall.call_xid} dm={dm} media={media} callXid={dmCall.call_xid}
          expanded={callExpanded} onMinimize={() => {
            setMinimizedCallXid(dmCall.call_xid);
            window.requestAnimationFrame(() => returnToCallButton.current?.focus());
          }} />
      ) : null}
      <div className={cn("min-h-0 min-w-0 flex-1 flex-col", callExpanded ? "hidden" : "flex")}>
      <header className="glass-toolbar liquid-lens-host relative flex min-h-14 items-center gap-2 border-b border-cloud-200 bg-white px-2.5 py-2 sm:gap-3 sm:px-4">
        <GlassMaterial />
        <button
          type="button"
          onClick={() => setFullProfileOpen(true)}
          disabled={profileMember === null}
          className="relative rounded-full transition-transform hover:scale-105 disabled:cursor-default disabled:hover:scale-100"
          aria-label={`Open ${dm.name}'s full profile`}
        >
          <Avatar className="h-9 w-9">
            {dm.participants[0]?.avatarUrl != null ? (
              <AvatarImage src={dm.participants[0].avatarUrl} alt="" />
            ) : null}
            <AvatarFallback variant={dm.isGroup ? "initials" : "ghost"} className="text-[11px]">
              {dm.name.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {directPresence !== undefined ? (
            <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-px">
              <StatusDot status={directPresence} sizeClass="h-3 w-3" ringClass="ring-white" />
            </span>
          ) : null}
        </button>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1 truncate text-base font-semibold text-cloud-800">
            <span className="truncate">{dm.name}</span>
            <BadgeRow
              badges={verifiedBadgeOnly(primaryParticipant?.badges ?? [])}
              max={1}
            />
          </p>
          <p className="truncate text-[11px] text-cloud-500">
            {dm.isGroup
              ? `${dm.participants.length + 1} ${dm.participants.length === 0 ? "participant" : "participants"} · End-to-end encrypted`
              : `${directPresence === "online" ? "Online" : "Offline"} · End-to-end encrypted`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => void media.startCall({ context: "dm", xid: dm.xid, label: `${dm.name} call` })}
          disabled={anotherCallIsOpen || media.connecting}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-cloud-500"
          aria-label="Start voice call"
        >
          <Phone className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => void media.startCall({ context: "dm", xid: dm.xid, label: `${dm.name} call` }, true)}
          disabled={anotherCallIsOpen || media.connecting}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-cloud-500"
          aria-label="Start video call"
        >
          <Video className="h-4 w-4" />
        </button>
        <button type="button" onClick={loadPins} className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-lg", pinnedOpen ? "bg-strawberry-100 text-strawberry-700" : "text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600")} aria-label="Pinned messages"><Pin className="h-4 w-4" /></button>
        {dm.isGroup ? (
          <button type="button" onClick={() => setGroupOpen(true)} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-cloud-500 hover:bg-strawberry-50 hover:text-strawberry-600" aria-label="Manage group chat">
            <Users className="h-4 w-4" />
          </button>
        ) : null}
        <div className="hidden shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200 sm:inline-flex">
          <Lock className="h-3 w-3" />
          E2E
        </div>
      </header>

      {dmCall !== null ? (
        <button ref={returnToCallButton} type="button" className="call-return-banner" onClick={() => setMinimizedCallXid(null)}>
          <PhoneCall size={17} aria-hidden="true" /><span>Return to call</span><span>{thisDmConnected ? "Connected" : media.connecting ? "Joining…" : "Call open"}</span>
        </button>
      ) : null}

      {pinnedOpen ? <div className="border-b border-cloud-200 bg-cloud-50 px-4 py-3"><div className="mx-auto max-w-3xl"><div className="flex items-center justify-between"><p className="text-sm font-bold text-cloud-800">Pinned messages</p><button type="button" onClick={() => setPinnedOpen(false)} className="rounded-lg p-1 text-cloud-400 hover:bg-cloud-200"><X className="h-4 w-4" /></button></div><div className="mt-2 space-y-2">{live.messages.filter((message) => pinnedXids.includes(message.xid)).map((message) => <div key={message.xid} className="rounded-xl border border-cloud-200 bg-white px-3 py-2 text-sm"><strong>{message.authorName}</strong><p className="mt-1 line-clamp-2 text-cloud-600">{message.body}</p></div>)}{pinnedXids.length === 0 ? <p className="py-3 text-sm text-cloud-500">Nothing has been pinned in this conversation.</p> : null}</div></div></div> : null}
      {groupOpen ? (
        <GroupChatManager
          dm={dm}
          selfAccountXid={self.accountXid}
          friends={friends}
          onClose={() => setGroupOpen(false)}
          onChanged={() => onDmsChanged?.()}
        />
      ) : null}

      {timeline.length === 0 ? (
        <div className="flex flex-1 items-center justify-center px-6 py-12 text-center">
          <div className="max-w-sm">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-strawberry-100">
              <MessageCircle className="h-8 w-8 text-strawberry-600" />
            </div>
            <p className="text-base font-semibold text-cloud-800">
              You're connected with {dm.name}
            </p>
            <p className="mt-1 text-sm text-cloud-500">
              Messages are end-to-end encrypted on this device. Say hi.
            </p>
          </div>
        </div>
      ) : (
        <ScrollArea className="min-h-0 flex-1 px-2 sm:px-4">
          <div data-message-list className="space-y-3 py-4">
            <ul>
              {timeline.map((item, index) =>
                item.kind === "message" ? (
                  <MessageItem
                    key={item.key}
                    message={item.message}
                    grouped={groupsWithPrevious(item.message, timeline[index - 1]?.kind === "message" ? (timeline[index - 1] as { message: MessageView }).message : undefined)}
                    asMember={() => {
                      const author = item.message.authorXid === "me"
                        || item.message.authorXid === self.accountXid
                        ? selfFriend
                        : dm.participants.find((participant) => participant.xid === item.message.authorXid);
                      if (author === undefined) {
                        return {
                          name: item.message.authorName,
                          tag: item.message.authorXid,
                          avatarUrl: item.message.authorAvatarUrl,
                          status: "offline",
                          bio: null,
                          roles: [],
                          mutualSpaces: [],
                        };
                      }
                      return {
                        ...friendToMemberProfile(author),
                        name: item.message.authorName,
                        avatarUrl: item.message.authorAvatarUrl ?? author.avatarUrl,
                      };
                    }}
                    resolveMention={(username) => {
                      const target = [selfFriend, ...dm.participants].find((participant) => participant.username?.toLowerCase() === username.toLowerCase());
                      return target === undefined ? undefined : friendToMemberProfile(target);
                    }}
                    onReact={(_msg, emoji) =>
                      void live.toggleReact(item.message.xid, emoji)
                    }
                    onReply={setReplyTo}
                    onTogglePin={() => void live.togglePin(item.message.xid)}
                    onEdit={(message, body) => live.edit(message.xid, body)}
                    onDelete={async (message) => {
                      if (window.confirm("Delete this message?")) await live.delete(message.xid);
                    }}
                  />
                ) : (
                  <DmCallHistoryItem
                    key={item.key}
                    call={item.call}
                    selfAccountXid={self.accountXid}
                  />
                ),
              )}
            </ul>
            <div ref={timelineEnd} aria-hidden="true" />
          </div>
        </ScrollArea>
      )}

      <div className="border-t border-cloud-200 bg-white px-2 py-2 sm:px-4 sm:py-3">
        {replyTo !== null && <ComposerReplyPreview message={replyTo} onCancel={() => setReplyTo(null)} />}
        {quickEdit !== null ? <div className="mb-2 flex items-center gap-2 rounded-xl bg-violet-50 px-3 py-2 text-xs text-violet-700"><span className="min-w-0 flex-1 truncate">Editing your message</span><button type="button" onClick={() => { setQuickEdit(null); setDraft(""); }} className="rounded-md p-1 hover:bg-violet-100" aria-label="Cancel edit"><X className="h-3.5 w-3.5" /></button></div> : null}
        <div className="liquid-lens-host relative flex items-end gap-1.5 rounded-2xl border border-cloud-200 bg-white px-2 py-2 shadow-sm sm:gap-2 sm:px-3">
          <GlassMaterial radius={16} />
          <textarea
            ref={composerInput}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => setComposerFocused(true)}
            onBlur={() => setComposerFocused(false)}
            onKeyDown={(e) => {
              if (e.nativeEvent.isComposing) return;
              if (e.key === "ArrowUp" && draft.length === 0) {
                const latestOwn = [...messages].reverse().find((message) => message.authorXid === self.accountXid || message.authorXid === "me");
                if (latestOwn !== undefined) {
                  e.preventDefault();
                  setQuickEdit(latestOwn);
                  setDraft(latestOwn.body);
                  return;
                }
              }
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                const body = draft.trim();
                if (body.length === 0 || live.status !== "ready") return;
                if (quickEdit !== null) void live.edit(quickEdit.xid, body);
                else void live.send(body, [], replyTo?.xid);
                setDraft("");
                setReplyTo(null);
                setQuickEdit(null);
              }
            }}
            disabled={live.status !== "ready"}
            placeholder={dmComposerPlaceholder(
              live.status === "ready",
              composerFocused,
              dm.isGroup
                ? dm.name
                : `@${primaryParticipant?.username ?? dm.name}`,
            )}
            rows={1}
            className="min-h-9 min-w-0 max-h-40 flex-1 resize-none whitespace-pre-wrap break-words border-none bg-transparent py-2 text-sm leading-5 text-cloud-800 outline-none placeholder:text-cloud-400"
          />
          <ComposerActions
            onInsert={insertIntoDraft}
            onAttach={queueFiles}
          />
          <Button
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full"
            disabled={draft.trim().length === 0 || live.status !== "ready"}
            onClick={() => {
              const body = draft.trim();
              if (body.length === 0) return;
              if (quickEdit !== null) void live.edit(quickEdit.xid, body);
              else void live.send(body, [], replyTo?.xid);
              setDraft("");
              setReplyTo(null);
              setQuickEdit(null);
            }}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <p className={cn("mt-1 text-[10px]", uploadError === null ? "text-cloud-400" : "text-red-600")}>
          {uploadError ?? `Enter to send · Shift+Enter for a new line · drag files anywhere to upload · @${self.name}`}
        </p>
      </div>
      </div>
      {profileMember !== null ? (
        <UserProfileModal
          open={fullProfileOpen}
          member={profileMember}
          onOpenChange={setFullProfileOpen}
        />
      ) : null}
    </main>
  );
}

function GroupChatManager({
  dm,
  selfAccountXid,
  friends,
  onClose,
  onChanged,
}: {
  dm: DmView;
  selfAccountXid: string;
  friends: FriendView[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const owner = dm.ownerAccountXid === selfAccountXid;
  const [name, setName] = useState(dm.name);
  const [busyXid, setBusyXid] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const participantXids = new Set(dm.participants.map((participant) => participant.xid));
  const available = friends.filter((friend) => !participantXids.has(friend.xid));
  const full = dm.participants.length + 1 >= 10;

  const mutate = (key: string, work: Promise<unknown>, success: string): void => {
    setBusyXid(key);
    setMessage(null);
    void work
      .then(() => {
        setMessage(success);
        onChanged();
      })
      .catch((reason: unknown) => setMessage(reason instanceof Error ? reason.message : "That change couldn't be saved."))
      .finally(() => setBusyXid(null));
  };

  return (
    <div className="fixed inset-0 z-[170] flex items-center justify-center bg-cloud-950/55 p-4 backdrop-blur-sm" onMouseDown={onClose}>
      <section className="max-h-[min(720px,calc(100dvh-2rem))] w-full max-w-lg overflow-y-auto rounded-3xl border border-cloud-200 bg-white p-5 shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-strawberry-100 text-strawberry-700"><Users className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1"><h2 className="text-lg font-bold text-cloud-900">Group chat</h2><p className="text-xs text-cloud-500">{dm.participants.length + 1} of 10 people · End-to-end encrypted</p></div>
          <button type="button" onClick={onClose} className="rounded-xl p-2 text-cloud-400 hover:bg-cloud-100" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        {owner ? <div className="mt-5 flex gap-2"><Input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} aria-label="Group name" /><Button disabled={busyXid !== null || name.trim().length === 0 || name.trim() === dm.name} onClick={() => mutate("name", api.dms.rename(dm.xid, name.trim()), "Group name updated.")}>Save</Button></div> : null}

        <div className="mt-5"><p className="text-xs font-bold uppercase tracking-wide text-cloud-500">People</p><div className="mt-2 divide-y divide-cloud-100 rounded-2xl border border-cloud-200">
          <div className="flex items-center gap-3 px-3 py-3"><Avatar className="h-9 w-9"><AvatarFallback>Y</AvatarFallback></Avatar><div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-cloud-800">You</p><p className="text-xs text-cloud-400">@you</p></div>{owner ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-[10px] font-bold text-amber-700"><Crown className="h-3 w-3" /> Owner</span> : null}</div>
          {dm.participants.map((participant) => <div key={participant.xid} className="flex items-center gap-3 px-3 py-3"><Avatar className="h-9 w-9">{participant.avatarUrl != null ? <AvatarImage src={participant.avatarUrl} alt="" /> : null}<AvatarFallback>{participant.name.slice(0, 2)}</AvatarFallback></Avatar><div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-cloud-800">{participant.name}</p><p className="truncate text-xs text-cloud-400">@{participant.username ?? participant.xid}</p></div>{dm.ownerAccountXid === participant.xid ? <Crown className="h-4 w-4 text-amber-500" aria-label="Group owner" /> : null}{owner && dm.ownerAccountXid !== participant.xid ? <button type="button" disabled={busyXid !== null} onClick={() => mutate(participant.xid, api.dms.removeParticipant(dm.xid, participant.xid), `Removed @${participant.username ?? participant.name}.`)} className="rounded-lg p-2 text-red-500 hover:bg-red-50" aria-label={`Remove ${participant.name}`}><UserMinus className="h-4 w-4" /></button> : null}</div>)}
        </div></div>

        {owner ? <div className="mt-5"><div className="flex items-center justify-between"><p className="text-xs font-bold uppercase tracking-wide text-cloud-500">Add friends</p>{full ? <span className="text-xs font-semibold text-strawberry-600">Group is full</span> : null}</div><div className="mt-2 max-h-52 space-y-1 overflow-y-auto">{available.map((friend) => <div key={friend.xid} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-cloud-50"><Avatar className="h-8 w-8">{friend.avatarUrl != null ? <AvatarImage src={friend.avatarUrl} alt="" /> : null}<AvatarFallback>{friend.name.slice(0, 2)}</AvatarFallback></Avatar><div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-cloud-800">{friend.name}</p><p className="truncate text-xs text-cloud-400">@{friend.username ?? friend.xid}</p></div><Button size="sm" variant="outline" disabled={full || busyXid !== null} onClick={() => mutate(friend.xid, api.dms.addParticipant(dm.xid, friend.xid), `Added @${friend.username ?? friend.name}.`)}><UserPlus className="mr-1 h-3.5 w-3.5" /> Add</Button></div>)}{available.length === 0 ? <p className="rounded-xl bg-cloud-50 px-3 py-4 text-center text-sm text-cloud-500">Everyone you can add is already here.</p> : null}</div></div> : (
          <Button variant="outline" className="mt-5 w-full text-red-600" disabled={busyXid !== null} onClick={() => mutate("leave", api.dms.removeParticipant(dm.xid, selfAccountXid), "You left the group.")}>Leave group</Button>
        )}
        {message !== null ? <p className="mt-4 rounded-xl bg-cloud-50 px-3 py-2 text-xs text-cloud-600">{message}</p> : null}
      </section>
    </div>
  );
}

function DmCallHistoryItem({
  call,
  selfAccountXid,
}: {
  call: CallHistoryEntry;
  selfAccountXid: string;
}) {
  const initiatedBySelf = call.initiator.account_xid === selfAccountXid;
  const other = call.participants.find(
    (participant) => participant.account_xid !== selfAccountXid,
  );
  const otherName = other?.display_name ?? call.initiator.display_name;
  const declined = call.participants.some(
    (participant) => participant.state === "declined",
  );
  const missed = call.participant_state === "missed";

  let description: string;
  if (missed) {
    description = `Missed ${call.with_video ? "video " : ""}call from ${call.initiator.display_name}.`;
  } else if (call.participant_state === "declined") {
    description = `You declined ${call.initiator.display_name}'s ${call.with_video ? "video " : ""}call.`;
  } else if (call.status === "cancelled") {
    description = declined
      ? `${otherName} declined your ${call.with_video ? "video " : ""}call.`
      : `You started a ${call.with_video ? "video " : ""}call that wasn't answered.`;
  } else {
    const durationStart = call.answered_at ?? call.started_at;
    const durationEnd = call.ended_at ?? call.started_at;
    description = `${initiatedBySelf ? "You" : call.initiator.display_name} started a ${call.with_video ? "video " : ""}call that lasted ${formatCallDuration(durationStart, durationEnd)}.`;
  }

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-lg px-2 py-2 text-sm",
        missed ? "text-rose-500" : "text-cloud-500",
      )}
      role="note"
      aria-label={description}
    >
      <span
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
          missed
            ? "bg-rose-500/10 text-rose-500"
            : "bg-emerald-500/10 text-emerald-500",
        )}
      >
        {missed ? (
          <PhoneMissed className="h-4 w-4" />
        ) : (
          <PhoneCall className="h-4 w-4" />
        )}
      </span>
      <p className="min-w-0 leading-relaxed">
        <span className={cn("font-medium", missed ? "text-rose-500" : "text-cloud-600")}>
          {description}
        </span>{" "}
        <span className="whitespace-nowrap text-xs text-cloud-400">
          {formatCallTimestamp(call.started_at)}
        </span>
      </p>
    </li>
  );
}

function formatCallDuration(startedAt: string, endedAt: string): string {
  const seconds = Math.max(
    1,
    Math.round((Date.parse(endedAt) - Date.parse(startedAt)) / 1_000),
  );
  if (seconds < 60) return `${seconds} ${seconds === 1 ? "second" : "seconds"}`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (remainder === 0) return `${hours} ${hours === 1 ? "hour" : "hours"}`;
  return `${hours} ${hours === 1 ? "hour" : "hours"} ${remainder} ${remainder === 1 ? "minute" : "minutes"}`;
}

function formatCallTimestamp(startedAt: string): string {
  const started = new Date(startedAt);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (left: Date, right: Date): boolean =>
    left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate();
  const time = started.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (sameDay(started, today)) return `Today at ${time}`;
  if (sameDay(started, yesterday)) return `Yesterday at ${time}`;
  return `${started.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: started.getFullYear() === today.getFullYear() ? undefined : "numeric",
  })} at ${time}`;
}

function DmCallStage({
  dm,
  media,
  callXid,
  expanded,
  onMinimize,
}: {
  dm: DmView;
  media: ReturnType<typeof useMediaSession>;
  callXid: string;
  expanded: boolean;
  onMinimize: () => void;
}) {
  const self = useSelf();
  const [watchOpen, setWatchOpen] = useState(false);
  const [watchService, setWatchService] = useState<WatchService>("youtube");
  const [watchUrl, setWatchUrl] = useState("");
  const [watchError, setWatchError] = useState<string | null>(null);
  const incoming = media.incomingCall?.call_xid === callXid ? media.incomingCall : null;
  const outgoing = media.outgoingCall?.call_xid === callXid ? media.outgoingCall : null;
  const active = media.activeCall?.call_xid === callXid ? media.activeCall : null;
  const ownsMedia = media.target?.context === "dm" && media.target.xid === dm.xid;
  const participants = ownsMedia ? media.participants : [];
  const connected = active !== null && ownsMedia && media.connected;
  const selfMediaParticipant = participants.find(
    (item) => item.isLocal || item.identity === self.identity.accountXid,
  );
  const selfSpeaking = selfMediaParticipant?.isSpeaking === true
    && selfMediaParticipant.isMicrophoneEnabled;
  const selfMuted = selfMediaParticipant !== undefined
    && !selfMediaParticipant.isMicrophoneEnabled;
  const videoParticipants = participants.filter(
    (item) => participantVideoTrack(item) !== null,
  );
  const watchPartyIsHostedBySelf =
    media.watchParty?.hostIdentity === media.room?.localParticipant.identity;
  const status = incoming !== null
    ? `Incoming ${incoming.with_video ? "video" : "voice"} call`
    : outgoing !== null
      ? "Ringing…"
      : media.connecting
        ? "Joining call…"
        : connected
          ? "Connected · End-to-end encrypted"
          : "Call in progress";

  return (
    <CallSurface expanded hidden={!expanded} hasVideo={videoParticipants.length > 0}
      header={<CallHeader name={dm.name} avatarUrl={dm.participants[0]?.avatarUrl ?? null} group={dm.isGroup}
        connected={connected}
        subtitle={incoming !== null ? "Incoming call" : outgoing !== null ? "Calling…" : connected ? "Connected · End-to-end encrypted" : media.connecting ? "Joining encrypted call…" : "Not connected · Call in progress"}
        onMinimize={onMinimize} />}>
        {videoParticipants.length > 0 ? (
          <div className="call-video-grid mb-6 grid w-full max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2">
            {videoParticipants.map((item) => (
              <InlineCallVideoTile
                key={item.identity}
                participant={item}
                label={item.isLocal
                  ? self.identity.name
                  : (dm.participants.find((participant) => participant.xid === item.identity)?.name ?? dm.name)}
              />
            ))}
          </div>
        ) : null}
        <div className="call-people">
          <CallParticipantBubble
            name={self.identity.name}
            subtitle="You"
            avatarUrl={self.identity.avatarUrl}
            speaking={selfSpeaking}
            muted={selfMuted}
          />
          {dm.participants.map((participant) => {
            const connectedParticipant = participants.find((item) => item.identity === participant.xid);
            const speaking = connectedParticipant?.isSpeaking === true && connectedParticipant.isMicrophoneEnabled;
            const muted = connectedParticipant !== undefined && !connectedParticipant.isMicrophoneEnabled;
            return <CallParticipantBubble
              key={participant.xid}
              name={participant.name}
              subtitle={outgoing !== null
                ? "Ringing…"
                : incoming !== null
                  ? "Calling you"
                  : connectedParticipant === undefined
                    ? "Not connected"
                    : undefined}
              avatarUrl={participant.avatarUrl ?? null}
              speaking={speaking}
              muted={muted}
              pulsing={outgoing !== null || incoming !== null}
            />;
          })}
        </div>
        <p className="call-status" role="status">{status}</p>

        {active !== null && media.watchParty !== null ? (
          <div className="mt-4 flex w-full max-w-2xl flex-wrap items-center gap-3 rounded-xl bg-white/10 px-4 py-3 text-left ring-1 ring-white/15">
            <Popcorn className="h-4 w-4 shrink-0 text-grape-300" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-white">Watch party</p>
              <p className="truncate text-xs text-white/55">{media.watchParty.url}</p>
            </div>
            {!media.watchPartyAccepted ? (
              <button type="button" onClick={media.acceptWatchParty} className="rounded-lg bg-grape-500 px-3 py-2 text-xs font-bold text-white hover:bg-grape-400">
                Accept
              </button>
            ) : (
              <a href={media.watchParty.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-2 text-xs font-bold text-white ring-1 ring-white/15 hover:bg-white/20">
                Open service <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {watchPartyIsHostedBySelf ? (
              <button type="button" onClick={() => void media.endWatchParty()} className="rounded-lg bg-rose-500/15 px-3 py-2 text-xs font-bold text-rose-200 ring-1 ring-rose-400/30 hover:bg-rose-500/25">
                End
              </button>
            ) : null}
          </div>
        ) : null}

        {active !== null && watchOpen && media.watchParty === null ? (
          <div className="mt-4 w-full max-w-2xl rounded-xl bg-white/10 p-4 text-left ring-1 ring-white/15">
            <div className="flex flex-wrap gap-2">
              {DM_WATCH_SERVICES.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => setWatchService(item.id)}
                  className={cn(
                    "rounded-full px-3 py-1.5 text-xs font-bold ring-1 transition-colors",
                    watchService === item.id
                      ? "bg-grape-500 text-white ring-grape-400"
                      : "bg-white/5 text-white/70 ring-white/15 hover:bg-white/10",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Input
                value={watchUrl}
                onChange={(event) => setWatchUrl(event.target.value)}
                placeholder="Paste the exact show or video link"
                className="h-9 border-white/15 bg-black/20 text-white placeholder:text-white/35"
              />
              <Button
                size="sm"
                onClick={() => {
                  setWatchError(null);
                  void media
                    .startWatchParty(watchService, watchUrl)
                    .then(() => setWatchOpen(false))
                    .catch((cause: unknown) =>
                      setWatchError(
                        cause instanceof Error ? cause.message : "Invalid link.",
                      ),
                    );
                }}
              >
                Invite
              </Button>
            </div>
            <p className="mt-2 text-[11px] text-white/45">
              Everyone chooses whether to join through their own streaming account.
            </p>
            {watchError !== null ? (
              <p className="mt-1 text-xs font-medium text-rose-300">{watchError}</p>
            ) : null}
          </div>
        ) : null}

        <CallControls mode={incoming !== null ? "incoming" : outgoing !== null ? "outgoing" : "active"}
          connected={connected} connecting={media.connecting}
          microphoneEnabled={media.microphoneEnabled} cameraEnabled={media.cameraEnabled}
          deafened={media.deafened} screenShareEnabled={media.screenShareEnabled}
          watchActive={watchOpen || media.watchParty !== null}
          onAccept={() => { if (incoming !== null) void media.acceptCall(incoming); }}
          onDecline={() => { if (incoming !== null) void media.declineCall(incoming); }}
          onCancel={() => void media.cancelCall()} onMicrophone={() => void media.toggleMicrophone()}
          onCamera={() => void media.toggleCamera()} onDeafen={media.toggleDeafen}
          onScreenShare={() => void media.toggleScreenShare()} onWatch={() => setWatchOpen(value => !value)}
          onRejoin={() => void media.rejoinCall()} onLeave={() => void media.leave()} />
        {media.error !== null ? <CallError message={media.error} /> : null}
    </CallSurface>
  );
}

function InlineCallVideoTile({
  participant,
  label,
}: {
  participant: Participant;
  label: string;
}) {
  const track = participantVideoTrack(participant);
  if (track === null) return null;
  return <CallVideoTile track={track} label={label} screenShare={participantIsScreenSharing(participant)}
    speaking={participant.isSpeaking && participant.isMicrophoneEnabled} muted={!participant.isMicrophoneEnabled} />;
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}
